import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dna, Library, SearchCheck, ShieldOff } from "lucide-react";
import { Reveal, SectionHead } from "./Primitives";

const STEPS = [
  {
    id: "proteins", icon: Dna, tab: "Proteins",
    title: "Your body runs on tiny machines.",
    body: "Proteins are microscopic machines that do almost everything in a living body. Each one is a long chain of building blocks that folds into a specific 3D shape — and that shape decides what the protein does.",
    analogy: "Think of a key: the exact shape of its teeth decides which lock it opens.",
  },
  {
    id: "library", icon: Library, tab: "The shape library",
    title: "Scientists share the shapes they measure.",
    body: "Labs around the world measure protein shapes and upload them to public libraries like the Protein Data Bank. Many of these are cancer-related proteins — sometimes the normal version, sometimes a version with a mutation (a small change in the recipe).",
    analogy: "Like a huge shared archive of 3D blueprints, uploaded by thousands of different people.",
  },
  {
    id: "checks", icon: SearchCheck, tab: "What Morphogenesis does",
    title: "It inspects and compares those shapes.",
    body: "Morphogenesis is a research tool that checks whether a shape's measurements are consistent, compares a changed version against a normal reference to see how far apart they sit, and checks that what a file says is inside matches what's actually inside. It does this across 60 cancer-related gene families.",
    analogy: "Like a building inspector: measure the building, compare it to the reference plans, and flag where the label on the folder doesn't match the contents.",
  },
  {
    id: "limits", icon: ShieldOff, tab: "What it isn't",
    title: "It's a research tool — not a diagnosis.",
    body: "It doesn't diagnose people or pets, predict whether a treatment will work, or replace a doctor or vet. A shape looking different doesn't automatically mean it's harmful. Morphogenesis keeps different kinds of evidence separate so nobody mistakes one for another.",
    analogy: "A measuring tape tells you a wall is crooked. It can't tell you whether the house is safe to live in.",
  },
];

const WORDS = [
  ["Protein", "A tiny machine in the body, made from a folded chain."],
  ["Backbone", "The spine of that chain — what the 3D view traces."],
  ["Mutation", "A small change in the recipe, like a typo."],
  ["Reference", "The normal version used for comparison."],
  ["RMSD", "On average, how far apart two shapes sit (in ångströms, a ten-billionth of a meter)."],
  ["Label", "What a shape file says about itself — which can be wrong."],
];

export const Explainer = () => {
  const [i, setI] = useState(0);
  const s = STEPS[i];
  const Icon = s.icon;
  return (
    <section id="explainer" data-testid="explainer-section" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow="In plain English" title={<>What is this, <span className="text-[var(--muted)]">in simple terms?</span></>} body="No biology degree needed. Four short steps." />
        <div className="mt-14 grid lg:grid-cols-[0.8fr_1.2fr] gap-6">
          <div role="tablist" className="grid grid-cols-2 lg:grid-cols-1 gap-2 content-start">
            {STEPS.map((t, k) => (
              <button key={t.id} role="tab" aria-selected={k === i} onClick={() => setI(k)} data-testid={`explainer-tab-${t.id}`}
                className={`relative flex items-center gap-3 rounded-lg border px-4 py-4 text-left transition-colors ${k === i ? "border-[var(--teal)] bg-[var(--surface)]" : "border-[var(--line)] hover:border-[#1f7a6c]/50"}`}>
                <span className={`font-mono text-xs ${k === i ? "text-[var(--teal)]" : "text-[var(--muted)]"}`}>0{k + 1}</span>
                <span className={`text-sm sm:text-base font-medium ${k === i ? "text-[var(--text)]" : "text-[var(--muted)]"}`}>{t.tab}</span>
                {k === i && <motion.span layoutId="explainer-bar" className="tri-bar absolute left-0 right-0 bottom-0 h-[2px] rounded-b-lg" />}
              </button>
            ))}
          </div>
          <div className="relative grid rounded-xl border border-[var(--line)] bg-[var(--surface)] overflow-hidden">
            <AnimatePresence initial={false}>
              <motion.div key={s.id} data-testid="explainer-panel"
                initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                className="[grid-area:1/1] p-6 sm:p-10 flex flex-col">
                <span className="grid h-12 w-12 place-items-center rounded-lg bg-[#1f7a6c]/10 text-[var(--teal)]"><Icon className="h-6 w-6" /></span>
                <h3 className="font-display mt-6 text-2xl sm:text-3xl font-bold tracking-tight">{s.title}</h3>
                <p className="mt-4 text-base md:text-lg leading-relaxed text-[var(--muted)]">{s.body}</p>
                <p className="mt-auto pt-6 border-t border-dashed border-[var(--line)] text-sm md:text-base text-[var(--text)]"><span className="font-mono text-xs uppercase tracking-wider text-[var(--amber)] mr-2">Analogy</span>{s.analogy}</p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        <Reveal className="mt-14">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-[var(--teal)]">Six words that help</p>
          <dl className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-[var(--line)]">
            {WORDS.map(([w, d], k) => (
              <div key={w} data-testid={`explainer-word-${k}`} className="border-r border-b border-[var(--line)] p-5">
                <dt className="font-display text-lg font-bold">{w}</dt>
                <dd className="mt-1 text-sm text-[var(--muted)] leading-relaxed">{d}</dd>
              </div>
            ))}
          </dl>
        </Reveal>
      </div>
    </section>
  );
};
